/**
 * The two operator-editable values on the Intake and billing panel: the service
 * a lead says they want, and the market they're closest to.
 *
 * Both resolve **extension first, most recent call answer second, eligibility
 * form third, absent fourth**. The ordering is by how confirmed each source is: a
 * rep editing the row directly outranks what was said on a call, which outranks
 * what the lead's signup form inferred for itself.
 *
 * The form is in the chain because it is the *first* place either value exists.
 * Without it both rows sat blank for every lead who had not yet had an intro call
 * charted — which is what a CEO report and BAR-2597 each surfaced independently.
 */

import { Patient } from '@medplum/fhirtypes';
import { PatientAccessCallAnswers } from './patient-access-note';
import { INTERESTED_SERVICE_EXTENSION, MARKET_LOCATION_EXTENSION } from './extensions';

/** The intro-call beat ids these fall back to. `loc`, not `clinic`. */
const SERVICE_ANSWER_ID = 'procedure';
const MARKET_ANSWER_ID = 'loc';

/**
 * Writes go out as `valueString`, matching `EMPLOYER_EXTENSION` and the rest of
 * the string-valued patient extensions here. Reads also accept `valueCode`,
 * because both of these name a value from a closed list and an earlier draft of
 * the design specified `valueCode` — tolerating it costs one `??` and means a
 * value written by anything following that draft still displays.
 */
function readExtension(patient: Patient | undefined, url: string): string | undefined {
  const found = patient?.extension?.find((entry) => entry.url === url);

  return found?.valueString ?? found?.valueCode;
}

function readAnswer(answers: PatientAccessCallAnswers | undefined, id: string): string | undefined {
  const value = answers?.values?.[id];

  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

/**
 * A stored value outside the current option list is returned as-is rather than
 * dropped. Hiding it would make a renamed offering look like missing data, and
 * the row is a record of what a rep chose, not a validation surface.
 */
export function readInterestedService(
  patient: Patient | undefined,
  answers: PatientAccessCallAnswers | undefined,
  /**
   * From the lead's eligibility form — `readEligibilityIntakeSelections().procedure`.
   *
   * Last in the chain because it is the least confirmed: the form is the lead
   * choosing for themselves, a saved call answer is a rep confirming it out loud,
   * and the extension is a rep editing the row directly. It is in the chain at all
   * because it is the *first* place the choice exists, and without it the row was
   * blank for every lead who had not yet had an intro call charted.
   */
  eligibilityProcedure?: string
): string | undefined {
  return (
    readExtension(patient, INTERESTED_SERVICE_EXTENSION) ??
    readAnswer(answers, SERVICE_ANSWER_ID) ??
    (eligibilityProcedure && eligibilityProcedure.length > 0 ? eligibilityProcedure : undefined)
  );
}

/**
 * Third in the chain is the lead's eligibility form, mirroring
 * `readInterestedService`.
 *
 * This deliberately had *no* eligibility fallback before BAR-2597, because the
 * form's `nearest-location` named a market from bariendo-be's MySQL list while the
 * select offered the keys of `CLINIC_PHYSICIANS` — two hand-maintained lists that
 * overlapped on almost nothing, so feeding the form's value in would have shown a
 * market the rep could not select. Phase 1 made the Medplum `market`-coded
 * `Location` resources canonical for both, so the value now lands in the same
 * vocabulary the select offers.
 *
 * A value outside the current registry is still returned rather than dropped: a
 * retired market must read as what a rep chose, not as missing data.
 */
export function readMarketLocation(
  patient: Patient | undefined,
  answers: PatientAccessCallAnswers | undefined,
  /**
   * From `readEligibilityIntakeSelections().market`.
   *
   * Last because it is the least confirmed — the form infers a market from the
   * lead's ZIP, a saved call answer is a rep confirming it out loud, and the
   * extension is a rep editing the row directly. It is in the chain at all
   * because it is the *first* place a market exists for a lead.
   */
  eligibilityMarket?: string
): string | undefined {
  return (
    readExtension(patient, MARKET_LOCATION_EXTENSION) ??
    readAnswer(answers, MARKET_ANSWER_ID) ??
    (eligibilityMarket && eligibilityMarket.length > 0 ? eligibilityMarket : undefined)
  );
}

/**
 * The patient's extensions with one url set to `value`, as a new array.
 *
 * Returns the whole array rather than patching in place so the caller can hand
 * it straight to `updatePatient({ extension })` — and so this stays pure and
 * testable. Every other extension is preserved untouched; duplicates of the
 * target url collapse to one, since two values for one field is never right.
 */
export function withPatientSelection(patient: Patient, url: string, value: string): Patient['extension'] {
  const others = (patient.extension ?? []).filter((entry) => entry.url !== url);
  const index = (patient.extension ?? []).findIndex((entry) => entry.url === url);
  const replacement = { url, valueString: value };

  // Keep the row where it was, so an edit doesn't reshuffle the resource.
  if (index === -1) return [...others, replacement];

  return [...others.slice(0, index), replacement, ...others.slice(index)];
}
